import { useTranslation } from 'react-i18next';
import type { QrCode, QrCodeFile } from '../api/types';
import { formatBytes, formatDateTime } from '../format';

/** The file scans get, and the one waiting for its scan to replace it. */
export function FileVersions({
  qrCode,
  onDownload,
}: {
  qrCode: QrCode;
  onDownload: (file: QrCodeFile) => void;
}) {
  const { t, i18n } = useTranslation();
  const newest = qrCode.files[0];
  const shown = qrCode.files.filter((file) => file.isLive || file.status === 'Scanning');
  const hasLive = qrCode.files.some((file) => file.isLive);

  return (
    <div className="file-versions">
      {newest?.status === 'Scanning' && (
        <p className="file-versions__notice" role="status">
          <i className="pi pi-spin pi-spinner" aria-hidden="true" />
          <span>
            {t('qrCode.file.pending', { name: newest.fileName })} {!hasLive && t('qrCode.file.pendingFirst')}
          </span>
        </p>
      )}
      {(newest?.status === 'Malicious' || newest?.status === 'ScanFailed') && (
        <p className="file-versions__notice is-refused" role="alert">
          <i className="pi pi-exclamation-triangle" aria-hidden="true" />
          <span>{t('qrCode.file.refused', { name: newest.fileName })}</span>
        </p>
      )}
      {!hasLive && newest?.status !== 'Scanning' && <p className="text-body-secondary mb-1">{t('qrCode.file.noneLive')}</p>}

      <ul className="file-versions__list" aria-label={t('qrCode.file.files')}>
        {shown.map((file) => (
          <li key={file.id} className={file.isLive ? 'is-live' : undefined}>
            <span className={`file-status ${file.isLive ? 'is-clean' : 'is-scanning'}`}>
              {t(file.isLive ? 'qrCode.file.live' : 'qrCode.file.checking')}
            </span>
            <span className="file-versions__file">
              <span className="file-versions__name" title={file.fileName}>
                {file.fileName}
              </span>
              <span className="file-versions__meta">
                {formatBytes(file.sizeBytes, i18n.language)} &middot;{' '}
                {t('qrCode.createdBy', { date: formatDateTime(file.uploadedAt), name: file.uploadedByName ?? file.uploadedBy })}
              </span>
            </span>
            {file.isLive && (
              <button
                type="button"
                className="btn btn-sm btn-link p-0"
                aria-label={t('qrCode.file.download', { name: file.fileName })}
                title={t('qrCode.file.download', { name: file.fileName })}
                onClick={() => onDownload(file)}
              >
                <i className="pi pi-download" aria-hidden="true" />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
